import { initReveal } from '../common/reveal';
import { gsap } from 'gsap';

export function initCraftCaseHeroEntrance(): void {
    const hero = document.querySelector<HTMLElement>('.craft-hero');
    if (!hero || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const copyItems = Array.from(hero.querySelectorAll<HTMLElement>('.craft-hero__copy > *'));
    const screen = hero.querySelector<HTMLElement>('.craft-hero__screen-wrapper');
    const widgets = Array.from(hero.querySelectorAll<HTMLElement>('.craft-hero__widget, .craft-hero-card'));

    let settled = false;
    const settle = (): void => {
        if (settled) {
            return;
        }

        settled = true;
        gsap.set(copyItems, { autoAlpha: 1, y: 0 });
        if (screen) {
            gsap.set(screen, { autoAlpha: 1, x: 0, y: 0, rotateY: -10 });
        }
        if (widgets.length > 0) {
            gsap.set(widgets, { autoAlpha: 1, y: 0, scale: 1 });
        }
    };

    const timeline = gsap.timeline({ defaults: { ease: 'power3.out' }, onComplete: settle });

    if (copyItems.length > 0) {
        timeline.fromTo(copyItems, { autoAlpha: 0, y: 22 }, { autoAlpha: 1, y: 0, duration: 0.72, stagger: 0.075 });
    }

    if (screen) {
        timeline.fromTo(screen, { autoAlpha: 0, x: 48, y: 26, rotateY: -18 }, { autoAlpha: 1, x: 0, y: 0, rotateY: -10, duration: 0.95 }, '<0.08');
    }

    if (widgets.length > 0) {
        timeline.fromTo(widgets, { autoAlpha: 0, y: 18, scale: 0.96 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.62, stagger: 0.08 }, '<0.18');
    }

    window.setTimeout(settle, 1800);
}

export function initCraftCaseHeroInteractivity(): void {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const hero = document.querySelector<HTMLElement>('.craft-hero');
    if (!hero) return;

    const screenWrapper = hero.querySelector<HTMLElement>('.craft-hero__screen-wrapper');
    const widgetBack = hero.querySelector<HTMLElement>('.craft-hero__widget--back');
    const widgetFront = hero.querySelector<HTMLElement>('.craft-hero__widget--front');
    const widgetFrontRight = hero.querySelector<HTMLElement>('.craft-hero__widget--front-right');
    const projectCard = hero.querySelector<HTMLElement>('.craft-hero-card');
    const reflection = hero.querySelector<HTMLElement>('.craft-browser-frame__reflection');
    if (!screenWrapper) return;

    const baseRotateX = 6;
    const baseRotateY = -10;
    const baseRotateZ = -2;

    const onMouseMove = (e: MouseEvent) => {
        const rect = hero.getBoundingClientRect();
        
        // Calculate normalized mouse positions from -0.5 to 0.5 relative to hero container
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;

        // 1. Animate Main Screen Wrapper
        gsap.to(screenWrapper, {
            rotateX: baseRotateX - y * 6,
            rotateY: baseRotateY + x * 8,
            rotateZ: baseRotateZ + x * 1,
            x: x * 20,
            y: y * 20,
            duration: 0.8,
            ease: 'power2.out',
            overwrite: 'auto'
        });

        // 2. Animate Back Widget
        if (widgetBack) {
            gsap.to(widgetBack, {
                rotateX: baseRotateX - y * 10,
                rotateY: baseRotateY + x * 12,
                rotateZ: baseRotateZ + x * 1.5,
                x: x * 35,
                y: y * 35,
                duration: 0.8,
                ease: 'power2.out',
                overwrite: 'auto'
            });
        }

        // 3. Animate Front Widget
        if (widgetFront) {
            gsap.to(widgetFront, {
                rotateX: baseRotateX - y * 10,
                rotateY: baseRotateY + x * 12,
                rotateZ: baseRotateZ + x * 1.5,
                x: x * 35,
                y: y * 35,
                duration: 0.8,
                ease: 'power2.out',
                overwrite: 'auto'
            });
        }

        // 4. Animate Front Right Widget
        if (widgetFrontRight) {
            gsap.to(widgetFrontRight, {
                rotateX: baseRotateX - y * 10,
                rotateY: baseRotateY + x * 12,
                rotateZ: baseRotateZ + x * 1.5,
                x: x * 35,
                y: y * 35,
                duration: 0.8,
                ease: 'power2.out',
                overwrite: 'auto'
            });
        }

        // 5. Animate Floating Project Card
        if (projectCard) {
            gsap.to(projectCard, {
                rotateX: baseRotateX - y * 12,
                rotateY: baseRotateY + x * 14,
                rotateZ: baseRotateZ + x * 2,
                x: x * 35,
                y: y * 35,
                duration: 0.8,
                ease: 'power2.out',
                overwrite: 'auto'
            });
        }

        if (reflection) {
            // Translate the light reflection highlight
            gsap.to(reflection, {
                xPercent: x * 35,
                yPercent: y * 35,
                duration: 0.8,
                ease: 'power2.out',
                overwrite: 'auto'
            });
        }
    };

    const onMouseLeave = () => {
        // Smoothly restore base idle transforms
        const resetParams = {
            rotateX: baseRotateX,
            rotateY: baseRotateY,
            rotateZ: baseRotateZ,
            x: 0,
            y: 0,
            duration: 1.2,
            ease: 'power3.out',
            overwrite: 'auto'
        };

        gsap.to(screenWrapper, resetParams);
        if (widgetBack) gsap.to(widgetBack, resetParams);
        if (widgetFront) gsap.to(widgetFront, resetParams);
        if (widgetFrontRight) gsap.to(widgetFrontRight, resetParams);
        if (projectCard) gsap.to(projectCard, resetParams);

        if (reflection) {
            gsap.to(reflection, {
                xPercent: 0,
                yPercent: 0,
                duration: 1.2,
                ease: 'power3.out',
                overwrite: 'auto'
            });
        }
    };

    hero.addEventListener('mousemove', onMouseMove);
    hero.addEventListener('mouseleave', onMouseLeave);
}

export function initProductGalleries(): void {
    document.querySelectorAll<HTMLElement>('[data-prod-gallery]').forEach(gallery => {
        const imgs = Array.from(gallery.querySelectorAll<HTMLElement>('.prod-gallery__img'));
        const thumbs = Array.from(gallery.querySelectorAll<HTMLButtonElement>('.prod-gallery__thumb'));
        if (!imgs.length || !thumbs.length) return;

        const setActive = (index: number, moveFocus = false): void => {
            imgs.forEach((img, i) => img.classList.toggle('is-active', i === index));
            thumbs.forEach((thumb, i) => {
                thumb.classList.toggle('is-active', i === index);
                thumb.setAttribute('aria-selected', (i === index).toString());
                thumb.tabIndex = i === index ? 0 : -1;
            });
            if (moveFocus) thumbs[index]?.focus({ preventScroll: true });
        };

        const initialIndex = Math.max(0, thumbs.findIndex(thumb => thumb.classList.contains('is-active')));
        setActive(initialIndex);

        thumbs.forEach((thumb, i) => {
            thumb.addEventListener('click', () => setActive(i));
            thumb.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive(i); }
                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    setActive((i + 1) % thumbs.length, true);
                }
                if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    setActive((i - 1 + thumbs.length) % thumbs.length, true);
                }
            });
        });
    });
}

const initFloatingCta = (): void => {
    const fab = document.getElementById('projFab');
    if (!fab) {
        return;
    }

    const toggle = (): void => {
        fab.classList.toggle('is-visible', window.scrollY > 400);
    };

    toggle();
    window.addEventListener('scroll', toggle, { passive: true });
};

// Initialize
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initReveal({ selector: '#projPage [data-reveal]', threshold: 0.18 });
        initFloatingCta();
        initCraftCaseHeroEntrance();
        initCraftCaseHeroInteractivity();
        initProductGalleries();
    });
} else {
    initReveal({ selector: '#projPage [data-reveal]', threshold: 0.18 });
    initFloatingCta();
    initCraftCaseHeroEntrance();
    initCraftCaseHeroInteractivity();
    initProductGalleries();
}
